import * as React from "react";
import { Button as RBB } from "react-bootstrap";
import styled from "styled-components";
import * as Color from "color";

import ThemeContext from "src/utils/theme";
import Props from "./props";

const StyledButton = styled(RBB)`
  &button {
    outline: none;
  }
  &.btn {
    border-radius: 2px;
    &:active {
      box-shadow: rgba(0, 0, 0, 0) 0px 0px 0px 0px,
        rgba(0, 0, 0, 0) 0px 0px 0px 0px, rgba(0, 0, 0, 0.12) 0px 1px 1px 0px,
        rgb(8, 106, 216) 0px 0px 0px 1px, rgba(0, 0, 0, 0) 0px 0px 0px 0px,
        rgba(0, 0, 0, 0) 0px 0px 0px 0px, rgba(60, 66, 87, 0.08) 0px 2px 5px 0px !important;
    }
  }
  &.btn-sm {
    display: inline-block;
    justify-content: center;
    align-items: center;
    min-width: 40px;
    padding: 7px 14px;
    font-size: 14px;
    box-shadow: rgba(0, 0, 0, 0) 0px 0px 0px 0px,
      rgba(0, 0, 0, 0) 0px 0px 0px 0px, rgba(0, 0, 0, 0.12) 0px 1px 1px 0px,
      rgb(8, 106, 216) 0px 0px 0px 1px, rgba(0, 0, 0, 0) 0px 0px 0px 0px,
      rgba(0, 0, 0, 0) 0px 0px 0px 0px, rgba(60, 66, 87, 0.08) 0px 2px 5px 0px;
    &:active {
      box-shadow: rgba(0, 0, 0, 0) 0px 0px 0px 0px,
        rgba(0, 0, 0, 0) 0px 0px 0px 0px, rgba(0, 0, 0, 0.12) 0px 1px 1px 0px,
        rgb(8, 106, 216) 0px 0px 0px 1px, rgba(0, 0, 0, 0) 0px 0px 0px 0px,
        rgba(0, 0, 0, 0) 0px 0px 0px 0px, rgba(60, 66, 87, 0.08) 0px 2px 5px 0px;
    }
    .loading-spinner {
      height: 11px;
      width: 11px;
      border: 2px solid #ffffff;
      border-right-color: transparent;
      margin-left: 10px;
      margin-top: 1px;
    }
  }
  &.btn-xs {
    display: inline-block;
    align-items: center;
    justify-content: center;
    height: 23px !important;
    min-width: 40px;
    &:active {
      box-shadow: none;
    }
    .loading-spinner {
      height: 14px;
      width: 14px;
      border: 2px solid #ffffff;
      border-right-color: transparent;
    }
  }
  &.btn-md {
    display: inline-block;
    align-items: center;
    justify-content: center;
    min-width: 60px;
    &:active {
      box-shadow: rgba(0, 0, 0, 0) 0px 0px 0px 0px,
        rgba(0, 0, 0, 0) 0px 0px 0px 0px, rgba(0, 0, 0, 0.12) 0px 1px 1px 0px,
        rgb(8, 106, 216) 0px 0px 0px 1px, rgba(0, 0, 0, 0) 0px 0px 0px 0px,
        rgba(0, 0, 0, 0) 0px 0px 0px 0px, rgba(60, 66, 87, 0.08) 0px 2px 5px 0px;
    }
    .loading-spinner {
      height: 14px;
      width: 14px;
      border: 2px solid #ffffff;
      border-right-color: transparent;
    }
  }
  &.btn-lg {
    display: inline-block;
    align-items: center;
    justify-content: center;
    min-width: 60px;
    &:active {
      box-shadow: rgba(0, 0, 0, 0) 0px 0px 0px 0px,
        rgba(0, 0, 0, 0) 0px 0px 0px 0px, rgba(0, 0, 0, 0.12) 0px 1px 1px 0px,
        rgb(8, 106, 216) 0px 0px 0px 1px, rgba(0, 0, 0, 0) 0px 0px 0px 0px,
        rgba(0, 0, 0, 0) 0px 0px 0px 0px, rgba(60, 66, 87, 0.08) 0px 2px 5px 0px;
    }
    .loading-spinner {
      height: 18px;
      width: 18px;
      border: 2px solid #ffffff;
      border-right-color: transparent;
      margin-left: 15px;
      margin-top: 3px;
    }
  }
  &.btn-gray {
    .loading-spinner {
      border-color: #333;
      border-right-color: transparent;
    }
  }
  &.btn-default {
    .loading-spinner {
      border-color: #fff;
      border-right-color: transparent;
    }
  }
  &.btn-link {
    .loading-spinner {
      border-color: #337ab7;
      border-right-color: transparent;
    }
  }
`;

/**
 * Button
 * @param param0
 * Doc https://react-bootstrap.github.io/components/buttons/
 */
function Button({
  id,
  label,
  className,
  href,
  disabled = false,
  outline = false,
  block = false,
  active = false,
  themeColor = "info",
  size = "lg",
  type = "button",
  onClick,
  style = {},
  loading,
  styleLoader,
}: Props) {
  return (
    <ThemeContext.Consumer>
      {({ palette }) => {
        const color = palette[themeColor];
        const isLight = Color(color).isLight();
        return (
          <StyledButton
            id={id}
            bsSize={size}
            bsClass={className}
            type={type}
            disabled={loading ? true : disabled}
            active={active}
            block={block}
            style={{
              backgroundColor: outline ? "#FFFFFF" : color,
              color: outline ? color : isLight ? "#354052" : "#FFFFFF",
              border: outline ? `2px solid ${color}` : "none",
              ...style,
            }}
            href={href}
            onClick={onClick}
          >
            {loading ? (
              <>
                <span>{label}</span>
              </>
            ) : (
              label
            )}
          </StyledButton>
        );
      }}
    </ThemeContext.Consumer>
  );
}

/**
 * Button
 */
export default Button;



// WEBPACK FOOTER //
// ./src/presentations/elements/Button/index.tsx